import { clsx } from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import {
  ConnectedFacility,
  ConnectedFacilityOptimized,
  SourceTypeEnum,
} from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { StatusPill } from '../../../components/StatusPill';

import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { getStatusTextStyle } from '../../../utils/statusStyles';

const CardHeading: React.FC<React.ComponentPropsWithoutRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      {...props}
      className={clsx(
        className,
        'tw:!text-[16px] tw:font-semibold tw:text-zinc-900 tw:dark:text-zinc-100 tw:!mb-0'
      )}
    >
      {children}
    </p>
  );
};

export const ConnectionsCard: React.FC<{
  connection: ConnectedFacility | ConnectedFacilityOptimized;
  specialtyMap: Record<string, string>;
  isSelected?: boolean;
}> = ({ connection, specialtyMap, isSelected = false }) => {
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  return (
    <div
      className={twMerge(
        clsx(
          'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-brand-green-1 tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer',
          isSelected && 'tw:!bg-muted tw:!shadow-md'
        )
      )}
    >
      <div className="tw:p-3">
        <div className="tw:flex tw:justify-between tw:items-start">
          <CardHeading className="">
            <i className="far fa-fw fa-lg fa-handshake tw:mr-1" />
            {connection.is_auto_match && (
              <span className="tw:text-xs tw:text-brand-green-0 tw:mx-1">Auto-Match</span>
            )}
          </CardHeading>
          <div className="tw:flex tw:flex-col tw:items-end tw:gap-1">
            <StatusPill
              scope={WorkflowScopes.ConnectedFacility}
              status={connection.current_status || 'Unknown'}
            />
          </div>
        </div>
        {connection.current_reason && (
          <div
            className={twMerge(
              'tw:text-xs tw:flex tw:justify-end tw:gap-1',
              getStatusTextStyle(connection.current_status)
            )}
          >
            {connection.current_reason.title}
          </div>
        )}
        <div className="tw:lg:text-lg tw:font-medium">{connection.provider.display_name}</div>

        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:mb-3">
          <span className="tw:text-sm tw:text-brand-green-0">
            {specialtyMap[connection.opportunity?.specialty] || connection.provider.specialties}
          </span>
        </div>

        <div className="tw:text-sm tw:text-zinc-600 tw:mb-1">
          <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1">
            <i className="fas fa-fw fa-hospital tw:text-zinc-500" />
            <span>{connection.facility.name}</span>
          </div>
          <div className="tw:text-sm tw:flex tw:items-center tw:gap-2 tw:mb-1">
            <i className="fas fa-fw fa-map-marker-alt tw:text-zinc-500" />
            <span>{getFacilityLocationDisplayValue(connection.facility)}</span>
          </div>
          <div className="tw:bg-brand-green-3 tw:rounded-md tw:mt-3 tw:p-2">
            <div className="tw:text-sm tw:font-medium tw:mb-1 tw:flex tw:justify-between">
              Hourly Rates{' '}
              {connection.source_type === SourceTypeEnum.ORGANIZATION && (
                <span className={clsx('tw:ml-1', SOURCE_TYPE_ORGANIZATION_BADGE_STYLE)}>1099</span>
              )}
            </div>
            <div className="tw:grid tw:grid-cols-4 tw:gap-1">
              {!!connection.facility_rate_day && connection.facility_rate_day !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(connection.facility_rate_day)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Daytime</div>
                </div>
              )}
              {!!connection.facility_rate_night && connection.facility_rate_night !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(connection.facility_rate_night)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Overnight</div>
                </div>
              )}
              {!!connection.facility_rate_24h && connection.facility_rate_24h !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(connection.facility_rate_24h)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">24 hour</div>
                </div>
              )}
              {!!connection.facility_rate_call && connection.facility_rate_call !== 0 && (
                <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
                  {formatCurrencyPostfix(connection.facility_rate_call)}
                  <div className="tw:text-xs tw:italic tw:text-zinc-500">Call</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionsCard;
